import { io, Socket } from 'socket.io-client';
import { getAccessToken, clearAuthData, getUserId, getUserRole } from 'utils/sessionStorage';
import { shouldSuppressCamSessionInterrupt } from 'utils/camAuth';
import pageStatusService from 'services/pageStatusService';

export interface ChatMessage {
  message_id?: number;
  conversation_id: string;
  creator: string;
  from_id: number;
  to_id: number;
  content: string;
  status: string;
  created_at?: string;
  /** HEADER-DOCS-025: files sent from CAM (Documents rows); only in chat history responses. */
  cam_files?: CamChatFile[];
}

/** HEADER-DOCS-025: a CAM-sent chat file, downloadable through the Documents API. */
export interface CamChatFile {
  externalId: string;
  displayName: string;
  mimeType: string | null;
  sizeBytes: number | null;
}

export interface JoinRoomPayload {
  session_id: string;
  user_id: number;
  user_type: 'student' | 'counselor' | 'parent';
}

export interface SendMessagePayload {
  conversation_id: string;
  content: string;
  creator: string;
  status: string;
  student_id: number;
  from_id: number;
  to_id: number;
  counselor_id: number;
}

export interface TypingPayload {
  conversation_id: string;
  user_id: number;
  user_type: 'student' | 'counselor' | 'parent';
  action: 'start' | 'stop';
}

export interface ReviewPayload {
  conversation_id: string;
  module_id: string;
  user_id: number;
  user_type: 'student' | 'counselor' | 'parent';
  review_type: 'student_review' | 'counselor_review';
  is_reviewed: boolean;
}

export interface SessionData {
  student_id: number;
}

/**
 * Helper function to parse conversation_id format: module_id|counselor_id|student_id
 * Returns session room format: session_${studentId}
 */
export function parseConversationId(conversationId: string): { studentId: number; counselorId: number; moduleId: string } | null {
  try {
    const parts = conversationId.split('|');
    if (parts.length !== 3) {
      console.error('Invalid conversation_id format. Expected: module_id|counselor_id|student_id');
      return null;
    }
    
    const [moduleId, counselorIdStr, studentIdStr] = parts;
    const counselorId = parseInt(counselorIdStr, 10);
    const studentId = parseInt(studentIdStr, 10);
    
    if (isNaN(counselorId) || isNaN(studentId)) {
      console.error('Invalid counselor_id or student_id in conversation_id');
      return null;
    }
    
    return { moduleId, counselorId, studentId };
  } catch (error) {
    console.error('Error parsing conversation_id:', error);
    return null;
  }
}

/**
 * Helper function to create session room name from student ID (session = session_studentId)
 */
export function createSessionRoomName(studentId: number): string {
  return `session_${studentId}`;
}

/**
 * Helper function to parse session room format: session_${studentId} or legacy session_${studentId}-${counselorId}
 * Returns { studentId, counselorId? } or null if invalid
 */
export function parseSessionRoomId(sessionRoomId: string): { studentId: number; counselorId?: number } | null {
  try {
    // New format: session_studentId
    const newMatch = sessionRoomId.match(/^session_(\d+)$/);
    if (newMatch) {
      const studentId = parseInt(newMatch[1], 10);
      return isNaN(studentId) ? null : { studentId };
    }
    // Legacy format: session_studentId-counselorId
    const legacyMatch = sessionRoomId.match(/^session_(\d+)-(\d+)$/);
    if (legacyMatch) {
      const studentId = parseInt(legacyMatch[1], 10);
      const counselorId = parseInt(legacyMatch[2], 10);
      if (isNaN(studentId) || isNaN(counselorId)) return null;
      return { studentId, counselorId };
    }
    // console.error('Invalid session room format. Expected: session_${studentId} or session_${studentId}-${counselorId}');
    return null;
  } catch (error) {
    console.error('Error parsing session room ID:', error);
    return null;
  }
}

/**
 * Unified WebSocket Service for Gradmap
 * 
 * This service connects to the unified WebSocket gateway (/unified namespace)
 * and handles both chat and page status functionality using shared session rooms.
 * 
 * Key Features:
 * - Single connection for all WebSocket functionality
 * - Shared session rooms: session_${studentId}
 * - Chat events prefixed with 'chat:'
 * - Page status events prefixed with 'page:'
 * - Automatic session room management
 * - Conversation ID format: module_id|counselor_id|student_id
 * 
 * Usage:
 * ```javascript
 * // Connect to unified gateway
 * const socket = io('ws://localhost:3000/unified', { auth: { token: 'jwt-token' } });
 * 
 * // Chat functionality
 * socket.emit('chat:join_room', { conversation_id: '1.1|456|123', user_id: 123, user_type: 'student' });
 * socket.emit('chat:send_message', { conversation_id: '1.1|456|123', from_id: 123, to_id: 456, content: 'Hello!' });
 * 
 * // Page status functionality
 * socket.emit('page:join_session_room', { student_id: 123 });
 * socket.emit('page:update_page_progress', { student_id: 123, page_name: 'personal_info', action: 'complete' });
 * ```
 */
class WebSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private sessionData: SessionData | null = null;
  private hasJoinedSession = false;
  private pageProgressListeners = new Set<(data: any) => void>();
  private maxReconnectAttempts = 20;

  constructor() {
    this.initializeSocket();
  }

  private initializeSocket() {
    const token = getAccessToken();

    if (!token) {
      console.warn('No authentication token found for WebSocket connection');
      return;
    }

    try {
      this.socket = io(process.env.REACT_APP_SOCKET_URL + '/unified', {
        auth: {
          token: token,
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
      });

      this.setupEventListeners();
    } catch (error) {
      console.error('Failed to initialize WebSocket connection:', error);
    }
  }

  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('WebSocket: Connected successfully');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.rejoinCurrentSession();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket: Disconnected - reason:', reason);
      this.isConnected = false;
      // Keep sessionData so we can rejoin the same student room after reconnect.
      this.hasJoinedSession = false;
    });

    this.socket.on('page:page_progress_updated', (data: any) => {
      this.notifyPageProgress(data);
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket: Connection error:', error);
      this.isConnected = false;
    });

    this.socket.on('reconnect', (attemptNumber) => {
      // console.log('WebSocket: Reconnected after', attemptNumber, 'attempts');
      this.isConnected = true;
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('WebSocket: Reconnection error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('WebSocket: Reconnection failed after', this.maxReconnectAttempts, 'attempts');
    });

    // Listen for force logout event
    this.socket.on('auth:force_logout', (data: any) => {
      console.log('WebSocket: Force logout received', data);
      if (shouldSuppressCamSessionInterrupt()) {
        this.disconnect();
        return;
      }
      clearAuthData();
      this.disconnect();
      window.location.href = '/#/public/login';
    });

    // Add debug listeners for all events
    this.socket.onAny((eventName, ...args) => {
      // console.log('WebSocket: Event received:', eventName, args);
    });
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        this.isConnected = true;
        resolve();
        return;
      }

      if (!this.socket) {
        this.initializeSocket();
      }

      if (!this.socket) {
        reject(new Error('Failed to initialize socket'));
        return;
      }

      // If already connected, resolve immediately
      if (this.isConnected) {
        resolve();
        return;
      }

      // Wait for connection
      const onConnect = () => {
        this.socket?.off('connect', onConnect);
        this.socket?.off('connect_error', onError);
        resolve();
      };

      const onError = (error: any) => {
        this.socket?.off('connect', onConnect);
        this.socket?.off('connect_error', onError);
        reject(error);
      };

      this.socket.on('connect', onConnect);
      this.socket.on('connect_error', onError);

      // Set a timeout to avoid hanging forever
      setTimeout(() => {
        this.socket?.off('connect', onConnect);
        this.socket?.off('connect_error', onError);
        if (!this.isConnected) {
          reject(new Error('WebSocket connection timeout'));
        }
      }, 10000); // 10 second timeout
    });
  }

  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.resetSession(); // Reset session data on disconnect
    }
  }

  public joinRoom(payload: JoinRoomPayload): Promise<any> {
    const ROOM_JOIN_TIMEOUT_MS = 15000;

    return new Promise((resolve, reject) => {
      console.log('WebSocket: Joining room', payload);
      if (!this.socket || !this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('join_room acknowledgement timeout'));
      }, ROOM_JOIN_TIMEOUT_MS);

      this.socket.emit('chat:join_room', payload, (response: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (response && response.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Failed to join room'));
        }
      });
    });
  }

  public leaveRoom(payload: JoinRoomPayload): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      this.socket.emit('chat:leave_room', payload, (response: any) => {
        if (response && response.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Failed to leave room'));
        }
      });
    });
  }

  public sendMessage(payload: SendMessagePayload): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      this.socket.emit('chat:send_message', payload, (response: any) => {
        if (response && response.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Failed to send message'));
        }
      });
    });
  }

  public getMessages(conversation_id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      this.socket.emit('chat:get_messages', { conversation_id }, (response: any) => {
        if (response && response.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Failed to get messages'));
        }
      });
    });
  }

  public sendTyping(payload: TypingPayload) {
    if (!this.socket || !this.isConnected) {
      console.error('WebSocket: Cannot send typing - not connected');
      return;
    }

    // console.log('WebSocket: Emitting typing event');
    this.socket.emit('chat:typing', payload);
  }

  public readMessage(conversation_id: string, user_id: number, message_id: number) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('chat:read_message', {
      conversation_id,
      user_id,
      message_id,
    });
  }

  public getUnreadCount(conversation_id: string, from_id: number, to_id: number): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      this.socket.emit('chat:get_unread_count', { conversation_id, from_id, to_id }, (response: any) => {
        if (response && response.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Failed to get unread count'));
        }
      });
    });
  }

  public getRecentConversations(user_id: number): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      this.socket.emit('chat:get_recent_conversations', { user_id }, (response: any) => {
        if (response && response.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Failed to get recent conversations'));
        }
      });
    });
  }

  public sendReview(payload: ReviewPayload): Promise<any> {
    // console.log('WebSocket: Attempting to send review with payload:', payload);
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        console.error('WebSocket: Cannot send review - not connected');
        reject(new Error('WebSocket not connected'));
        return;
      }

      // console.log('WebSocket: Emitting send_review event');
      this.socket.emit('chat:send_review', payload, (response: any) => {
        // console.log('WebSocket: Send review response:', response);
        if (response && response.status === 'success') {
          // console.log('WebSocket: Review sent successfully');
          resolve(response);
        } else {
          console.error('WebSocket: Failed to send review:', response?.message);
          reject(new Error(response?.message || 'Failed to send review'));
        }
      });
    });
  }

  // Prefer the student id passed explicitly by the caller (e.g. counselor route
  // params.id). Falling back to the socket session is only a last resort: it can
  // be empty mid-switch or point at a previously selected student.
  private resolveStudentId(explicitStudentId?: number | string | null): number {
    const fromCaller = Number(explicitStudentId);
    if (explicitStudentId != null && Number.isFinite(fromCaller) && fromCaller > 0) {
      return fromCaller;
    }

    const fromSession = Number(this.sessionData?.student_id);
    if (Number.isFinite(fromSession) && fromSession > 0) {
      return fromSession;
    }

    const role = (getUserRole() || '').toLowerCase().trim();
    if (role === 'student' || !role) {
      const fromUser = Number(getUserId());
      if (Number.isFinite(fromUser) && fromUser > 0) {
        return fromUser;
      }
    }

    throw new Error('No student id for page status update');
  }

  private notifyPageProgress(data: any) {
    this.pageProgressListeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error('WebSocket: page progress listener failed', error);
      }
    });
  }

  private rejoinCurrentSession() {
    const studentId = this.sessionData?.student_id;
    if (!studentId) return;
    this.hasJoinedSession = false;
    this.joinSession(studentId).catch((error) => {
      console.error('WebSocket: Failed to rejoin session room', error);
    });
  }

  private emitWithAck(event: string, payload: any, timeoutMs = 8000): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not connected'));
        return;
      }

      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error('WebSocket ack timeout'));
        }
      }, timeoutMs);

      this.socket.emit(event, payload, (response: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (response?.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'WebSocket request failed'));
        }
      });
    });
  }

  // Join session room (session = session_studentId; counselor/parent join the student's session)
  public async joinSession(studentId: number): Promise<void> {
    if (!this.socket || !this.socket.connected) {
      await this.connect();
    }

    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    if (this.hasJoinedSession && this.sessionData?.student_id === studentId && this.socket.connected) {
      return;
    }

    this.sessionData = { student_id: studentId };

    await this.emitWithAck('page:join_session_room', this.sessionData);
    this.hasJoinedSession = true;
  }

  // Persist via REST. The backend broadcasts `page:page_progress_updated` to
  // session_{studentId} after saving, so other open clients (the student's tab)
  // update in real time without depending on this client's socket state.
  public async finishReviewAndCompleteCounselor(
    pageName: string,
    counselorCompleted: boolean,
    explicitStudentId?: number | string | null,
  ): Promise<void> {
    const studentId = this.resolveStudentId(explicitStudentId);
    const counselorId = Number(getUserId());
    const saved = await pageStatusService.markCounselorVerified(
      studentId,
      pageName,
      counselorId,
      counselorCompleted
    );
    this.notifyPageProgress({
      action: 'verify',
      page_name: pageName,
      student_id: studentId,
      result: {
        ...saved,
        counselor_verified: saved?.counselor_verified ?? counselorCompleted,
      },
    });
  }

  // Persist via REST so the checkbox survives reload even if the socket is down.
  // Nav bar updates from the local notify; the backend broadcasts to the
  // student's session room for other connected clients.
  public async finishReviewAndComplete(
    pageName: string,
    studentCompleted: boolean,
    explicitStudentId?: number | string | null,
  ): Promise<void> {
    const studentId = this.resolveStudentId(explicitStudentId);
    const saved = await pageStatusService.markStudentCompleted(
      studentId,
      pageName,
      studentCompleted
    );
    this.notifyPageProgress({
      action: 'complete',
      page_name: pageName,
      student_id: studentId,
      result: {
        ...saved,
        student_completed: saved?.student_completed ?? studentCompleted,
      },
    });
  }

  // Leave session room
  public leaveSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Socket not connected'));
        return;
      }

      if (!this.hasJoinedSession || !this.sessionData) {
        // console.log('No active session to leave');
        resolve();
        return;
      }

      this.socket.emit('page:leave_session_room', this.sessionData, (response: any) => {
        if (response.status === 'success') {
          // console.log('✅ Left session room:', response.data?.room || 'unknown');
          this.resetSession();
          resolve();
        } else {
          reject(new Error(response.message || 'Failed to leave session'));
        }
      });
    });
  }

  // Check if user has joined a session
  public hasJoinedSessionRoom(): boolean {
    return this.hasJoinedSession;
  }

  // Get current session data
  public getCurrentSessionData(): SessionData | null {
    return this.sessionData;
  }

  // Reset session data (useful for logout)
  public resetSession(): void {
    this.hasJoinedSession = false;
    this.sessionData = null;
  }

  // Cleanup on logout: leave session and disconnect
  public async cleanupOnLogout(): Promise<void> {
    try {
      // Leave session room if joined
      if (this.hasJoinedSessionRoom()) {
        await this.leaveSession();
      }
      // Disconnect WebSocket
      this.disconnect();
      // console.log('✅ WebSocket cleanup completed on logout');
    } catch (error) {
      console.error('Error during WebSocket cleanup on logout:', error);
      // Still disconnect even if leaveSession fails
      this.disconnect();
    }
  }

  // Update page progress
  public onUpdatePageProgress(callback: (data: any) => void) {
    this.pageProgressListeners.add(callback);
  }

  public onForceLogout(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('auth:force_logout', callback);
    }
  }

  public offUpdatePageProgress(callback?: (data: any) => void) {
    if (callback) {
      this.pageProgressListeners.delete(callback);
    } else {
      this.pageProgressListeners.clear();
    }
  }

  // Ping/Pong functionality for connection health checks
  public ping(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      this.socket.emit('ping', {}, (response: any) => {
        if (response && response.status === 'success') {
          resolve(response);
        } else {
          reject(new Error(response?.message || 'Ping failed'));
        }
      });
    });
  }

  public onPong(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('pong', callback);
    }
  }

  public offPong() {
    if (this.socket) {
      this.socket.off('pong');
    }
  }

  // Event listeners for receiving messages
  public onNewMessage(callback: (message: ChatMessage) => void) {
    if (this.socket) {
      this.socket.on('chat:new_message', callback);
    }
  }

  public onUserJoined(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('chat:user_joined', callback);
    }
  }

  public onUserTyping(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('chat:user_typing', callback);
    }
  }

  public onMessageRead(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('chat:message_read', callback);
    }
  }

  public onReviewUpdate(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('chat:review_update', callback);
    }
  }

  // Remove event listeners
  public offNewMessage() {
    if (this.socket) {
      this.socket.off('chat:new_message');
    }
  }

  public offUserJoined() {
    if (this.socket) {
      this.socket.off('chat:user_joined');
    }
  }

  public offUserTyping() {
    if (this.socket) {
      this.socket.off('chat:user_typing');
    }
  }

  public offMessageRead() {
    if (this.socket) {
      this.socket.off('chat:message_read');
    }
  }

  public offReviewUpdate() {
    if (this.socket) {
      this.socket.off('chat:review_update');
    }
  }

  public isSocketConnected(): boolean {
    return this.isConnected;
  }

  public getSocket(): Socket | null {
    return this.socket;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
